import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { adminBluetoothLowBalanceNotifyPreview } from '../../api';

const { Text, Paragraph } = Typography;

const BluetoothLowBalanceNotifyPreview = () => {
  const [ignoreSubscription, setIgnoreSubscription] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runQuery = async () => {
    setLoading(true);
    try {
      const res = await adminBluetoothLowBalanceNotifyPreview({
        ignore_subscription: ignoreSubscription,
      });
      if (res?.success) {
        setResult(res.data || null);
        message.success(res.msg || '查询完成');
      } else {
        setResult(null);
        message.error(res?.msg || '查询失败');
      }
    } catch (e) {
      setResult(null);
      const isTimeout = e.code === 'ECONNABORTED' || String(e.message || '').includes('timeout');
      if (isTimeout) {
        message.error('查询超时：蓝牙表较多时扫描较慢，请确认后端已部署或稍后重试');
      } else if (e.response?.status === 404) {
        message.error('接口不存在(404)：请确认后端已部署 bluetooth-low-balance-notify-preview 路由');
      } else {
        message.error(e.response?.data?.msg || e.message || '查询失败');
      }
    } finally {
      setLoading(false);
    }
  };

  const list = Array.isArray(result?.list) ? result.list : [];
  const wouldCallList = list.filter((r) => r.would_call);

  const columns = [
    { title: '设备ID', dataIndex: 'device_id', width: 80 },
    { title: '表号', dataIndex: 'meter_no', width: 120, ellipsis: true },
    { title: 'fang', dataIndex: 'fang', width: 200, ellipsis: true },
    {
      title: '预估剩余(度)',
      dataIndex: 'estimated_remaining',
      width: 110,
      render: (v) => (v != null ? Number(v).toFixed(2) : '-'),
    },
    {
      title: '预估日均',
      dataIndex: 'daily_kwh',
      width: 90,
      render: (v) => (v != null ? Number(v).toFixed(2) : '-'),
    },
    {
      title: '阈值',
      dataIndex: 'threshold_kwh',
      width: 70,
      render: (v) => (v != null ? Number(v).toFixed(1) : '-'),
    },
    {
      title: '上次抄表剩余',
      dataIndex: 'anchor_remaining',
      width: 110,
      render: (v) => (v != null ? Number(v).toFixed(2) : '-'),
    },
    { title: '上次抄表时间', dataIndex: 'anchor_time', width: 160, render: (v) => v || '-' },
    { title: '抄表条数', dataIndex: 'record_count', width: 80 },
    {
      title: '默认6度/天',
      dataIndex: 'used_default_daily',
      width: 90,
      render: (v) => (v ? <Tag color="orange">是</Tag> : <Tag>否</Tag>),
    },
    { title: '手机号', dataIndex: 'phone', width: 120 },
    {
      title: '已订阅',
      dataIndex: 'subscribed',
      width: 70,
      render: (v) => (v ? <Tag color="green">是</Tag> : <Tag>否</Tag>),
    },
    {
      title: '会外呼',
      dataIndex: 'would_call',
      width: 80,
      render: (v) => (v ? <Tag color="red">是</Tag> : <Tag>否</Tag>),
    },
    { title: '跳过原因', dataIndex: 'skip_reason', width: 180, ellipsis: true },
  ];

  return (
    <div>
      <PageTitle title="蓝牙低电通知查询" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="按正式巡检口径模拟：抄表 log 预估剩余 ≤ 阈值，且 low_balance_notify_sent=0，且语音总开关开启。"
        description={
          <Paragraph style={{ marginBottom: 0 }}>
            上线前可勾选「忽视订阅」评估全量低电表；上线后取消勾选即为真实外呼口径。与 YJ 远程表查询独立，不影响其他功能。
            设备较多时扫描可能需要 30 秒～2 分钟，请耐心等待。
          </Paragraph>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Space wrap align="center">
          <Checkbox
            checked={ignoreSubscription}
            onChange={(e) => setIgnoreSubscription(e.target.checked)}
          >
            忽视是否订阅（上线前测试用）
          </Checkbox>
          <Button type="primary" loading={loading} onClick={runQuery}>
            {loading ? '扫描中…' : '查询预计通知'}
          </Button>
          {result?.generated_at && (
            <Text type="secondary">生成于 {result.generated_at}</Text>
          )}
        </Space>
      </Card>

      {result && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Space wrap size="large">
              <Statistic title="预计通话条数" value={result.expected_call_count ?? 0} />
              <Statistic title="预计人数" value={result.expected_people_count ?? 0} />
              <Statistic title="涉及设备数" value={result.expected_device_count ?? 0} />
              <Statistic title="已扫蓝牙表" value={result.scanned_devices ?? 0} />
              <Statistic title="预估低电设备" value={result.low_balance_devices ?? 0} />
              <Statistic title="低电但已提醒过" value={result.already_notified_low ?? 0} />
            </Space>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">
                语音总开关：
                {result.voice_notify_enabled ? (
                  <Tag color="green" style={{ marginLeft: 8 }}>已开启</Tag>
                ) : (
                  <Tag color="default" style={{ marginLeft: 8 }}>未开启（预计 0 通）</Tag>
                )}
                {ignoreSubscription ? (
                  <Tag color="orange" style={{ marginLeft: 8 }}>当前忽视订阅</Tag>
                ) : (
                  <Tag style={{ marginLeft: 8 }}>按订阅校验</Tag>
                )}
              </Text>
            </div>
          </Card>

          <Card
            title={`明细（预估低电 ${list.length} 行，其中会外呼 ${wouldCallList.length} 行）`}
          >
            {list.length === 0 ? (
              <Text type="secondary">
                已扫描 {result.scanned_devices ?? 0} 台蓝牙表，当前无预估低电设备（或抄表 log 不足、预估剩余仍高于阈值）。
              </Text>
            ) : (
              <Table
              rowKey={(r, idx) => `${r.device_id}-${r.phone}-${idx}`}
              columns={columns}
              dataSource={list}
              size="small"
              scroll={{ x: 1500 }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
            />
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default BluetoothLowBalanceNotifyPreview;
